import { User } from '@/types'

const TOKEN_KEY = 'admin_token'
const USER_KEY = 'admin_user'
const DEFAULT_LEVEL = 100

/** JWT segments are base64url; `atob` requires standard base64. */
function decodeJwtPayloadSegment(segment: string): { exp?: number } {
  let sanitized = segment.replace(/-/g, '+').replace(/_/g, '/')
  const padding = sanitized.length % 4
  if (padding) {
    sanitized += '='.repeat(4 - padding)
  }
  return JSON.parse(atob(sanitized))
}

function normalizeLevel(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return Math.floor(value)
  }
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10)
    if (Number.isFinite(parsed) && parsed >= 0) {
      return parsed
    }
  }
  return DEFAULT_LEVEL
}

export function getToken(): string | null {
  const token = localStorage.getItem(TOKEN_KEY)
  if (!token) return null

  if (isTokenExpired(token)) {
    removeToken()
    return null
  }

  return token
}

function isTokenExpired(token: string): boolean {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return true
    const payload = decodeJwtPayloadSegment(parts[1])
    if (!payload.exp) return false
    const bufferSeconds = 60
    return Date.now() >= (payload.exp - bufferSeconds) * 1000
  } catch {
    return true
  }
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
}

export function getUser(): User | null {
  const userStr = localStorage.getItem(USER_KEY)
  if (!userStr) return null
  
  try {
    const raw = JSON.parse(userStr) as User
    return { ...raw, level: normalizeLevel(raw?.level) }
  } catch {
    return null
  }
}

export function setUser(user: User): void {
  const normalized: User = {
    ...user,
    level: normalizeLevel(user?.level),
  }
  localStorage.setItem(USER_KEY, JSON.stringify(normalized))
}

export function isAuthenticated(): boolean {
  const token = getToken()
  const user = getUser()
  return !!token && !!user && user.role === 'admin'
}

export async function logout(options?: { redirect?: boolean }): Promise<void> {
  try {
    const token = getToken()
    if (token) {
      const { api } = await import('./api')
      await api.logout()
    }
  } catch (error) {
    console.warn('Failed to logout from server', error)
  } finally {
    removeToken()
    if (options?.redirect !== false) {
      window.location.href = '/login'
    }
  }
}

export function useAuth() {
  return {
    token: getToken(),
    user: getUser(),
    isAuthenticated: isAuthenticated(),
    logout
  }
}
